import { browserCacheVersions } from '@/config/browserCacheVersions';
import {
  readVersionedLocalStorage,
  removeVersionedLocalStorage,
  removeVersionedLocalStorageByPrefix,
  writeVersionedLocalStorage
} from '@/utils/versionedStorage';

import api from '../Jobs/API';

const EXPIRY_TIME = 3600 * 1000; // 1 hour in milliseconds
const UI_PREFERENCES_CACHE_PREFIX = 'uiPreferences:';

// Preference payloads vary by source and the API client does not expose a generic response type.
// Preserve that existing dynamic contract while validating cached sortable-column data at runtime.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UiPreferencesResponse = any;
type UiSettingsParams = Record<string, unknown> & { source?: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isValidSortableColumn = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.label === 'string' &&
  typeof value.sortable === 'boolean' &&
  typeof value.canToggle === 'boolean' &&
  typeof value.enabled === 'boolean';

const isValidUiPreferencesResponse = (value: unknown): value is UiPreferencesResponse => {
  if (!isRecord(value) || !isRecord(value.res) || !Array.isArray(value.res.ui_preferences)) {
    return false;
  }

  return value.res.ui_preferences.every((preference) => {
    if (!isRecord(preference) || preference.sortable_columns == null) return true;
    return (
      Array.isArray(preference.sortable_columns) &&
      preference.sortable_columns.every(isValidSortableColumn)
    );
  });
};

const cacheKey = (source: string): string => `${UI_PREFERENCES_CACHE_PREFIX}${source}`;

const legacyCacheKeys = (source: string): string[] => [
  `userPreferences:${source}`,
  `userPreferences:${source}:expiry`
];

const cacheOptions = (source: string) => ({
  legacyKeys: legacyCacheKeys(source),
  ttlMs: EXPIRY_TIME,
  validate: isValidUiPreferencesResponse,
  version: browserCacheVersions.uiPreferences
});

export const fetchPreferences = async (source: string): Promise<UiPreferencesResponse> => {
  const cachedResponse = readVersionedLocalStorage<UiPreferencesResponse>(
    cacheKey(source),
    cacheOptions(source)
  );
  if (cachedResponse) return cachedResponse;

  const response = await api.getUISettings({ source });
  if (isValidUiPreferencesResponse(response)) {
    writeVersionedLocalStorage(cacheKey(source), response, cacheOptions(source));
  }
  return response;
};

export const clearPreferenceCache = (source: string): void => {
  removeVersionedLocalStorage(cacheKey(source), cacheOptions(source));
};

export const clearAllPreferenceCache = (): void => {
  removeVersionedLocalStorageByPrefix(UI_PREFERENCES_CACHE_PREFIX);
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('userPreferences')) {
      localStorage.removeItem(key);
    }
  });
};

export const updateUiSettings = async (
  params: UiSettingsParams,
  onError?: (message: string) => void
): Promise<void> => {
  const { source } = params;
  try {
    await api.postUISettings({ ui_preference: params });
    if (source) {
      clearPreferenceCache(source);
    }
  } catch (error) {
    console.log(error);
    if (onError && error instanceof Error) {
      onError(error.message);
    }
  }
};
